import { Map as MLMap, addProtocol, setWorkerUrl, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre v6 は ESM のモジュールワーカーを使う。バンドラ経由だと自動解決できないので、
// Vite にワーカーを別ファイルとしてバンドルさせ、その URL を渡す。
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { Protocol } from 'pmtiles';
import { layers, namedFlavor } from '@protomaps/basemaps';
import { config, type TileSource } from '../config';

/**
 * タイル取得元の抽象化。
 * どれを選んでもアプリ側は「MapLibre のスタイル」を受け取るだけ。
 * 公開後に無料枠を超えたら VITE_TILE_SOURCE を変えるだけで切り替えられる。
 */
export function resolveStyle(source: TileSource): string | StyleSpecification {
  switch (source) {
    case 'maptiler':
      if (!config.maptilerKey) throw new Error('VITE_MAPTILER_KEY が未設定です');
      return `https://api.maptiler.com/maps/streets-v2/style.json?key=${config.maptilerKey}`;

    case 'pmtiles': {
      if (!config.pmtilesUrl) throw new Error('VITE_PMTILES_URL が未設定です');
      // R2 などに置いた .pmtiles をブラウザから Range リクエストで直接読む（Functions を通さない）
      addProtocol('pmtiles', new Protocol().tile);
      return {
        version: 8,
        glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
        sprite: 'https://protomaps.github.io/basemaps-assets/sprites/v4/light',
        sources: {
          protomaps: {
            type: 'vector',
            url: `pmtiles://${config.pmtilesUrl}`,
            attribution:
              '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: layers('protomaps', namedFlavor('light'), { lang: 'ja' }),
      };
    }

    case 'openfreemap':
    default:
      // キー不要・無料（SLA なし）。帰属表示はスタイルに含まれる
      return 'https://tiles.openfreemap.org/styles/liberty';
  }
}

export function createMap(container: HTMLElement): MLMap {
  setWorkerUrl(maplibreWorkerUrl);
  const map = new MLMap({
    container,
    style: resolveStyle(config.tileSource),
    center: config.initialCenter,
    zoom: config.initialZoom,
    pitch: 55,
    maxPitch: 80,
    attributionControl: { compact: true },
    // モバイルの発熱・電池対策: 高DPI端末でも描画解像度を最大2倍に抑える
    pixelRatio: Math.min(window.devicePixelRatio, 2),
  });

  map.on('error', (e) => console.warn('[map]', e.error?.message ?? e));
  // スタイルが参照しているのにスプライトに無いアイコン（OpenFreeMap の一部 POI）は透明画像で埋めて警告を抑える。
  // v6 では styleimagemissing イベントでは今回の要求に間に合わず警告が出るので、リゾルバで補う
  map.setMissingStyleImageResolver((id) => {
    if (!map.hasImage(id)) map.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) });
  });
  return map;
}
